import React from 'react';
import TabelaCrud from '../components/TabelaCrud';
import { getCurrentTenantId } from '../services/api';
import { useCreches } from '../hooks/useCreches';
import { useCriancas } from '../hooks/useCriancas';

interface ProjetoPedagogico { id: number; creche_id: number; versao: string; diretrizes: string; }
interface PlanejamentoAtividade { id: number; creche_id: number; faixa_etaria: string; campo_experiencia: string; atividade: string; objetivo: string; data: string; }
interface AvaliacaoIndividual { id: number; creche_id: number; aluno_id: number; campo_experiencia: string; relatorio: string; data: string; }
interface PortfolioDigital { id: number; creche_id: number; aluno_id: number; titulo: string; descricao: string; arquivo_url: string; }

// Campos de Experiência da BNCC (Educação Infantil) — espinha dorsal do trabalho pedagógico.
const CAMPOS_EXPERIENCIA_BNCC = [
  'O eu, o outro e o nós',
  'Corpo, gestos e movimentos',
  'Traços, sons, cores e formas',
  'Escuta, fala, pensamento e imaginação',
  'Espaços, tempos, quantidades, relações e transformações',
];

export default function PedagogicoPage() {
  const { options } = useCreches();
  const { criancas } = useCriancas();
  const tenantScoped = Boolean(getCurrentTenantId());

  const nomeCrianca = (id: number) => criancas.find((c) => c.id === id)?.nome || `#${id}`;

  return (
    <div className="stack-cadastros">
      <TabelaCrud<ProjetoPedagogico>
        titulo="Pedagógico — Projeto Pedagógico"
        endpoint="/pedagogico/projeto"
        colunas={[{ chave: 'id', label: 'ID' }, { chave: 'versao', label: 'Versão' }, { chave: 'diretrizes', label: 'Diretrizes' }]}
        itemVazio={{ creche_id: 0, versao: '', diretrizes: '' }}
        validateItem={(i) => ((!tenantScoped && !i.creche_id) || !i.versao || !i.diretrizes ? 'Preencha versão e diretrizes.' : null)}
        renderFormulario={(i, onChange) => (
          <>
            <label className={tenantScoped ? 'campo-tenant-oculto' : undefined}>Creche<select value={i.creche_id ?? 0} onChange={(e) => onChange('creche_id', Number(e.target.value))}><option value={0}>Selecione...</option>{options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></label>
            <label>Versão<input value={i.versao ?? ''} onChange={(e) => onChange('versao', e.target.value)} /></label>
            <label>Diretrizes<textarea rows={3} value={i.diretrizes ?? ''} onChange={(e) => onChange('diretrizes', e.target.value)} /></label>
          </>
        )}
      />

      <TabelaCrud<PlanejamentoAtividade>
        titulo="Pedagógico — Planejamento de Atividades"
        endpoint="/pedagogico/planejamento"
        colunas={[{ chave: 'id', label: 'ID' }, { chave: 'campo_experiencia', label: 'Campo de Experiência (BNCC)' }, { chave: 'faixa_etaria', label: 'Faixa Etária' }, { chave: 'atividade', label: 'Atividade' }, { chave: 'data', label: 'Data' }]}
        itemVazio={{ creche_id: 0, faixa_etaria: 'bercario', campo_experiencia: '', atividade: '', objetivo: '', data: '' }}
        validateItem={(i) => ((!tenantScoped && !i.creche_id) || !i.atividade || !i.data ? 'Preencha atividade e data.' : null)}
        renderFormulario={(i, onChange) => (
          <>
            <div className="linha-campos linha-2">
              <label className={tenantScoped ? 'campo-tenant-oculto' : undefined}>Creche<select value={i.creche_id ?? 0} onChange={(e) => onChange('creche_id', Number(e.target.value))}><option value={0}>Selecione...</option>{options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></label>
              <label>Faixa etária<select value={i.faixa_etaria ?? 'bercario'} onChange={(e) => onChange('faixa_etaria', e.target.value)}><option value="bercario">Berçário</option><option value="maternal">Maternal</option><option value="pre_escola">Pré-escola</option></select></label>
            </div>
            <label>Campo de Experiência (BNCC)<select value={i.campo_experiencia ?? ''} onChange={(e) => onChange('campo_experiencia', e.target.value)}><option value="">Selecione...</option>{CAMPOS_EXPERIENCIA_BNCC.map((c) => <option key={c} value={c}>{c}</option>)}</select></label>
            <label>Atividade<input value={i.atividade ?? ''} onChange={(e) => onChange('atividade', e.target.value)} /></label>
            <label>Objetivo de aprendizagem<textarea rows={2} value={i.objetivo ?? ''} onChange={(e) => onChange('objetivo', e.target.value)} /></label>
            <label>Data<input type="date" value={i.data ?? ''} onChange={(e) => onChange('data', e.target.value)} /></label>
          </>
        )}
      />

      <TabelaCrud<AvaliacaoIndividual>
        titulo="Pedagógico — Avaliação / Relatório de Desenvolvimento"
        endpoint="/pedagogico/avaliacoes"
        colunas={[{ chave: 'id', label: 'ID' }, { chave: 'aluno_id', label: 'Criança', render: (r) => nomeCrianca(r.aluno_id) }, { chave: 'campo_experiencia', label: 'Campo de Experiência (BNCC)' }, { chave: 'data', label: 'Data' }]}
        itemVazio={{ creche_id: 0, aluno_id: 0, campo_experiencia: '', relatorio: '', data: '' }}
        validateItem={(i) => ((!tenantScoped && !i.creche_id) || !i.aluno_id || !i.relatorio || !i.data ? 'Preencha criança, relatório e data.' : null)}
        renderFormulario={(i, onChange) => (
          <>
            <div className="linha-campos linha-3">
              <label className={tenantScoped ? 'campo-tenant-oculto' : undefined}>Creche<select value={i.creche_id ?? 0} onChange={(e) => onChange('creche_id', Number(e.target.value))}><option value={0}>Selecione...</option>{options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></label>
              <label>Criança<select value={i.aluno_id ?? 0} onChange={(e) => onChange('aluno_id', Number(e.target.value))}><option value={0}>Selecione...</option>{criancas.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}</select></label>
              <label>Data<input type="date" value={i.data ?? ''} onChange={(e) => onChange('data', e.target.value)} /></label>
            </div>
            <label>Campo de Experiência (BNCC)<select value={i.campo_experiencia ?? ''} onChange={(e) => onChange('campo_experiencia', e.target.value)}><option value="">Selecione...</option>{CAMPOS_EXPERIENCIA_BNCC.map((c) => <option key={c} value={c}>{c}</option>)}</select></label>
            <label>Relatório de desenvolvimento<textarea rows={3} value={i.relatorio ?? ''} onChange={(e) => onChange('relatorio', e.target.value)} /></label>
          </>
        )}
      />

      <TabelaCrud<PortfolioDigital>
        titulo="Pedagógico — Portfólio Digital"
        endpoint="/pedagogico/portfolio"
        colunas={[{ chave: 'id', label: 'ID' }, { chave: 'aluno_id', label: 'Criança', render: (r) => nomeCrianca(r.aluno_id) }, { chave: 'titulo', label: 'Título' }]}
        itemVazio={{ creche_id: 0, aluno_id: 0, titulo: '', descricao: '', arquivo_url: '' }}
        validateItem={(i) => ((!tenantScoped && !i.creche_id) || !i.aluno_id || !i.titulo ? 'Preencha criança e título.' : null)}
        renderFormulario={(i, onChange) => (
          <>
            <div className="linha-campos linha-2">
              <label className={tenantScoped ? 'campo-tenant-oculto' : undefined}>Creche<select value={i.creche_id ?? 0} onChange={(e) => onChange('creche_id', Number(e.target.value))}><option value={0}>Selecione...</option>{options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></label>
              <label>Criança<select value={i.aluno_id ?? 0} onChange={(e) => onChange('aluno_id', Number(e.target.value))}><option value={0}>Selecione...</option>{criancas.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}</select></label>
            </div>
            <label>Título<input value={i.titulo ?? ''} onChange={(e) => onChange('titulo', e.target.value)} /></label>
            <label>Descrição<textarea rows={2} value={i.descricao ?? ''} onChange={(e) => onChange('descricao', e.target.value)} /></label>
            <label>Arquivo/URL<input value={i.arquivo_url ?? ''} onChange={(e) => onChange('arquivo_url', e.target.value)} /></label>
          </>
        )}
      />
    </div>
  );
}
